import React from "react";
import styled from "styled-components";
import { Plus, Minus, X } from "lucide-react";
import { useLanguage } from "../contexts/language";
import { useOrder } from "../contexts/order";
import { unmodule } from "../util";

const fallbackMenuImage = unmodule(require("../assets/hebe-logo-vertical.png"));

const ItemContainer = styled.div`
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 12px;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  opacity: ${(props) => (props.$isOutOfStock ? 0.6 : 1)};
  pointer-events: ${(props) => (props.$isOutOfStock ? "none" : "auto")};
`;

const ImageWrapper = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f0f0f0;
  cursor: pointer;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const ItemDescription = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const StockInfo = styled.p`
  font-size: 12px;
  color: ${(props) => (props.$isOutOfStock ? "#999" : "#c9a961")};
  margin: 0;
  font-weight: 500;
`;

const PriceActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const Price = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #c9a961;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 2px;
`;

const QuantityButton = styled.button`
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuantityDisplay = styled.span`
  width: 20px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #333;
`;

const ZoomModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  padding: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
`;

const ZoomedImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

export const MenuItem = ({ item, isOutOfStock = false }) => {
  const { t } = useLanguage();
  const { cart, updateCartQuantity, updateCartItemNote } = useOrder();
  const [showZoom, setShowZoom] = React.useState(false);
  const cartItem = cart.find((cartLine) => cartLine.id === item.id);
  const quantity = cartItem?.quantity || 0;
  const note = cartItem?.note || "";

  const increaseQuantity = () => {
    updateCartQuantity(item.id, quantity + 1, item);
  };

  const decreaseQuantity = () => {
    updateCartQuantity(item.id, Math.max(0, quantity - 1));
  };

  const handleNoteChange = (event) => {
    updateCartItemNote(item.id, event.target.value);
  };

  return (
    <>
      <ItemContainer $isOutOfStock={isOutOfStock}>
        <ImageWrapper onClick={() => setShowZoom(true)}>
          <ItemImage src={item.image || fallbackMenuImage} alt={item.name} />
        </ImageWrapper>

        <ItemInfo>
          <ItemName>{item.name}</ItemName>
          <ItemDescription>{item.description}</ItemDescription>
          <StockInfo $isOutOfStock={isOutOfStock}>
            {isOutOfStock
              ? t.order.outOfStock
              : `${item.stock} ${t.order.available}`}
          </StockInfo>
          {!isOutOfStock && quantity > 0 && (
            <textarea
              value={note}
              onChange={handleNoteChange}
              maxLength={200}
              placeholder="Item note (max 200 chars)"
              style={{
                marginTop: "8px",
                width: "100%",
                minHeight: "54px",
                border: "1px solid #e5e5e5",
                borderRadius: "4px",
                padding: "6px 8px",
                fontSize: "12px",
                color: "#555",
                resize: "vertical",
              }}
            />
          )}
        </ItemInfo>

        <PriceActions>
          <Price>{item?.price?.formattedText || "Rp0"}</Price>

          {!isOutOfStock && (
            <ActionsWrapper>
              <QuantityControl>
                <QuantityButton onClick={decreaseQuantity}>
                  <Minus size={14} />
                </QuantityButton>
                <QuantityDisplay>{quantity}</QuantityDisplay>
                <QuantityButton onClick={increaseQuantity}>
                  <Plus size={14} />
                </QuantityButton>
              </QuantityControl>
            </ActionsWrapper>
          )}
        </PriceActions>
      </ItemContainer>

      {showZoom && (
        <ZoomModal onClick={() => setShowZoom(false)}>
          <CloseButton onClick={() => setShowZoom(false)}>
            <X size={24} />
          </CloseButton>
          <ZoomedImage
            src={item.image || fallbackMenuImage}
            alt={item.name}
            onClick={(e) => e.stopPropagation()}
          />
        </ZoomModal>
      )}
    </>
  );
};
